import { useEffect, useRef, useState } from 'react';
import { logout } from './api.js';
import PreviewPanel from './panels/PreviewPanel.jsx';
import DisplaysPanel from './panels/DisplaysPanel.jsx';
import ConnectionsPanel from './panels/ConnectionsPanel.jsx';
import AccountsPanel from './panels/AccountsPanel.jsx';
import AccountPanel from './panels/AccountPanel.jsx';
import ExportPanel from './panels/ExportPanel.jsx';
import EnrichmentPanel from './panels/EnrichmentPanel.jsx';
import GoodreadsPanel from './panels/GoodreadsPanel.jsx';

// Connections first and default: nothing else on this site does anything until
// one exists, and it is the tab somebody returns to when a shelf looks wrong.
//
// The nav is grouped because the second group is a different kind of thing:
// everything in "Your account" acts on the signed-in account, and everything
// under "Site admin" acts on other people's. Keeping them apart in the sidebar
// is the cheapest way to make that visible.
const SECTIONS = [
  {
    id: 'account',
    label: 'Your account',
    tabs: [
      { id: 'connections', label: 'Connections', icon: '🔗', Panel: ConnectionsPanel },
      { id: 'preview', label: 'Preview', icon: '✨', Panel: PreviewPanel },
      { id: 'displays', label: 'Displays', icon: '📺', Panel: DisplaysPanel },
      { id: 'export', label: 'Export', icon: '⬇️', Panel: ExportPanel },
      { id: 'account', label: 'Account', icon: '🧑', Panel: AccountPanel },
    ],
  },
  {
    id: 'site',
    label: 'Site admin',
    siteAdminOnly: true,
    tabs: [
      { id: 'accounts', label: 'Accounts', icon: '🗝️', Panel: AccountsPanel },
      { id: 'enrichment', label: 'Enrichment', icon: '🏛️', Panel: EnrichmentPanel },
      { id: 'goodreads', label: 'Goodreads', icon: '📚', Panel: GoodreadsPanel },
    ],
  },
];

const TABS = SECTIONS.flatMap((section) =>
  section.tabs.map((tab) => ({ ...tab, siteAdminOnly: Boolean(section.siteAdminOnly) }))
);

const DEFAULT_TAB = TABS[0].id;

// The tab lives in the hash rather than in state alone, so a reload keeps you
// where you were and a tab can be linked to. Not worth a router for three.
function tabFromHash() {
  const id = window.location.hash.replace(/^#\/?/, '');
  if (TABS.some((tab) => tab.id === id)) return id;
  // Arriving from a display's QR code: go straight to Displays, which is where
  // the code gets claimed.
  if (new URLSearchParams(window.location.search).has('pair')) return 'displays';
  return DEFAULT_TAB;
}

export default function AdminPage({ me, onSignedOut, onAccountChanged }) {
  const [tab, setTab] = useState(tabFromHash);
  // Only ever true on a narrow screen: the sidebar is always shown on a wide
  // one, and CSS decides which of the two this is. Kept here rather than
  // measured, so there is no width to keep in step with the stylesheet.
  const [menuOpen, setMenuOpen] = useState(false);
  const mobileBar = useRef(null);
  const account = me?.account;
  // The server decides this. Hiding the section is a courtesy to the person
  // looking at it, not a control: every route behind it checks the role again,
  // and refuses an impersonated session whatever this says.
  const siteAdmin = Boolean(me?.siteAdmin);

  useEffect(() => {
    const onHashChange = () => setTab(tabFromHash());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  // Land the resolved tab in the URL when it was not already there - arriving
  // from a pairing link picks Displays, and without this a reload would drop
  // back to the default. replaceState so it adds no history entry and fires no
  // hashchange.
  useEffect(() => {
    if (!window.location.hash) {
      window.history.replaceState(null, '', `#/${tab}`);
    }
  }, [tab]);

  // The open menu hangs below the header, which is stuck to the top of the
  // window - so it has to know how tall the header is. Measured the way the
  // impersonation banner measures itself, because the brand and the button can
  // each set the height depending on the platform's font, and any number
  // written here would be wrong on one of them. Zero on a wide screen, where
  // the header is display:none and nothing reads this.
  useEffect(() => {
    const node = mobileBar.current;
    if (!node) return undefined;

    const measure = () => {
      document.documentElement.style.setProperty('--mobile-bar-height', `${node.offsetHeight}px`);
    };
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty('--mobile-bar-height');
    };
  }, []);

  // Escape closes the menu, which is the only way out of it for a keyboard
  // once it is open - the toggle is the first thing in the header, not the
  // last, so tabbing past the end of the nav does not reach it.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [menuOpen]);

  function selectTab(id) {
    window.location.hash = `#/${id}`;
    setTab(id);
    // Picking a tab is the end of what the menu is for. Leaving it open would
    // cover the panel it just switched to.
    setMenuOpen(false);
  }

  async function handleSignOut() {
    try {
      await logout();
    } catch (error) {
      console.error('Sign out failed', error);
    }
    onSignedOut();
  }

  const visible = SECTIONS.filter((section) => siteAdmin || !section.siteAdminOnly);
  // A tab that is no longer allowed - a link kept from a session that had the
  // role, or a role since revoked - falls back to the default rather than
  // rendering a panel whose every request would be refused.
  const requested = TABS.find((candidate) => candidate.id === tab);
  const active = requested && (siteAdmin || !requested.siteAdminOnly) ? requested : TABS[0];

  return (
    <div className="admin-shell">
      {/* Narrow screens only - display:none on a wide one, so it takes no grid
          cell there and the sidebar keeps the first column. */}
      <header className="admin-topbar-mobile" ref={mobileBar}>
        <p className="eyebrow admin-brand">Litbyt</p>
        <button
          type="button"
          className={`admin-menu-toggle${menuOpen ? ' open' : ''}`}
          aria-expanded={menuOpen}
          aria-controls="admin-nav"
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          onClick={() => setMenuOpen((open) => !open)}
        >
          <span className="admin-menu-bars" aria-hidden="true">
            <span />
            <span />
            <span />
          </span>
        </button>
      </header>

      {/* Tapping the page behind the open menu closes it. Decoration for
          assistive technology, which has the toggle and Escape instead. */}
      {menuOpen && (
        <div className="admin-nav-scrim" aria-hidden="true" onClick={() => setMenuOpen(false)} />
      )}

      <nav
        id="admin-nav"
        className={`admin-nav${menuOpen ? ' open' : ''}`}
        aria-label="Sections"
      >
        <p className="eyebrow admin-brand admin-brand-wide">Litbyt</p>
        {visible.map((section) => (
          <div className="admin-nav-section" key={section.id}>
            <p className="admin-nav-heading">{section.label}</p>
            <ul>
              {section.tabs.map(({ id, label, icon }) => (
                <li key={id}>
                  <button
                    type="button"
                    className={`admin-nav-item${id === active.id ? ' active' : ''}`}
                    aria-current={id === active.id ? 'page' : undefined}
                    onClick={() => selectTab(id)}
                  >
                    {/* Decoration, so it is hidden from screen readers rather
                        than read out in front of every label. */}
                    <span className="admin-nav-icon" aria-hidden="true">
                      {icon}
                    </span>
                    {label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))}

        <div className="admin-nav-footer">
          {account && <p className="admin-account">{account.email}</p>}
          <button type="button" className="admin-link ghost" onClick={handleSignOut}>
            Sign out
          </button>
        </div>
      </nav>

      <main className="admin-content">
        <header className="topbar">
          <div>
            <p className="eyebrow">Admin</p>
            <h1>{active.label}</h1>
          </div>
        </header>

        <active.Panel me={me} onAccountChanged={onAccountChanged} />
      </main>
    </div>
  );
}
